import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InputField from "../components/ui/InputField";
import { Button } from "../components/ui";
import SelectUser from "../main/component/SelectUser";
import ProjectAction from "../actions/ProjectAction";
import CurrencyPicker from "../main/component/CurrencyPicker";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { navigateTo } from "../Navigator";
import { deleteProject } from "../api/ProjectApi";
import { getAppHome } from "../api/api";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import { ItemWrapper } from "../main/component/ProjectForm";
import ProjectPicker from "../main/component/ProjectPicker";
import { ColorPicker, colorCodes } from "../board/components/TaskDetail/ColorPicker";
import {
  CurrencyDollarIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UserPlusIcon,
  BriefcaseIcon,
  ChartPieIcon,
  BanknotesIcon,
  ShoppingCartIcon,
  BuildingOffice2Icon,
  LifebuoyIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";

const WORKSPACE_TYPE_OPTIONS: {
  value: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
}[] = [
    { value: "SALES", label: "Sales", Icon: CurrencyDollarIcon },
    { value: "PROJECTS", label: "Projects", Icon: Squares2X2Icon },
    { value: "PARTNERSHIPS", label: "Partnerships", Icon: UserGroupIcon },
    { value: "HIRING", label: "Hiring", Icon: UserPlusIcon },
    { value: "JOB_SEARCH", label: "Job search", Icon: BriefcaseIcon },
    { value: "INVESTOR", label: "Investor", Icon: ChartPieIcon },
    { value: "FUNDRAISING", label: "Fundraising", Icon: BanknotesIcon },
    { value: "ORDERS", label: "Orders", Icon: ShoppingCartIcon },
    { value: "REAL_ESTATE", label: "Real estate", Icon: BuildingOffice2Icon },
    { value: "SUPPORT", label: "Support", Icon: LifebuoyIcon },
    { value: "CUSTOM", label: "Custom", Icon: WrenchScrewdriverIcon },
  ];

const GeneralSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const isAdmin = currentUser.get("admin");
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));

  // Use tenantInfo to detect Jira tenant (same check as SaleForm)
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";
  const hasNoProjects = !project.get("projects") || project.get("projects").size === 0;

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [pipelineName, setPipelineName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const projectName = project.get("name");



  useEffect(() => {
    setPipelineName(projectName || "");
  }, [projectName]);

  const handleUpdateField = (fieldId: string, fieldValue: any) => {
    dispatch(ProjectAction.updateProjectField(fieldId, fieldValue));
  };

  const handleDelete = () => {
    setShowConfirmDelete(true);
  };

  const deleteCurrentProject = () => {
    deleteProject(project.get("id")).then(async () => {
      await dispatch(ProjectAction.getProjects());
      const appUrl = "/workspaces";
      navigateTo({
        jiraUrlParams: {
          relativeUrl: getAppHome(),
        },
        appUrl,
        navigate,
      });
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await dispatch(ProjectAction.updateProject(project.toJS()));
      await dispatch(ProjectAction.getProjects());
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="py-4 max-w-5xl">
      <div className="px-4 sm:px-0">
        <h3 className="text-lg font-semibold text-gray-900">{t("General")}</h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
          {t("Change the settings for your current Pipeline")}
        </p>
      </div>
      <div className="mt-6">
        <div className="mt-2">
          <InputField
            label={t("Pipeline Name")}
            value={pipelineName}
            onChange={(value) => {
              setPipelineName(value);
              handleUpdateField("name", value);
            }}
            placeholder={t("e.g., Sales pipeline")}
            disabled={!isAdmin}
          />
        </div>
        <div className="mt-2">
          <label className="block text-sm font-semibold text-gray-800 py-2">{t("Type")}</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-2xl">
            {WORKSPACE_TYPE_OPTIONS.map((opt) => {
              const selected = (project.get("type") || "SALES") === opt.value;
              const IconComponent = opt.Icon;
              const wsColor = project.get("color") || colorCodes[0].code;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => isAdmin && handleUpdateField("type", opt.value)}
                  disabled={!isAdmin}
                  data-testid={`workspace-type-${opt.value}`}
                  className={`text-left rounded-lg border-2 p-3 transition flex items-center gap-3 ${selected
                    ? "bg-blue-50"
                    : "border-gray-200 hover:border-gray-300 bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                    }`}
                  style={selected ? { borderColor: wsColor } : undefined}
                >
                  <span
                    className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg"
                    style={
                      selected
                        ? { backgroundColor: wsColor + "20", color: wsColor }
                        : { backgroundColor: "#f3f4f6", color: "#4b5563" }
                    }
                    aria-hidden
                  >
                    <IconComponent className="w-5 h-5" />
                  </span>
                  <span className="font-medium text-gray-900 truncate">{t(opt.label)}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="mt-2">
          <label className="block text-sm font-semibold text-gray-800 py-2">{t("Color")}</label>
          <ColorPicker
            pickerId="workspace-color"
            color={
              colorCodes.find((c) => c.code === project.get("color"))?.color ||
              colorCodes.find((c) => c.color === project.get("color"))?.color ||
              colorCodes[0].color
            }
            onSelectItem={(option) => handleUpdateField("color", option.code)}
          />
        </div>
        {isJira && (
          <ItemWrapper>
            <ProjectPicker
              title={t("Projects")}
              projects={project.get("projects")}
              error={isJira && hasNoProjects}
              onChange={(projects) => {
                handleUpdateField("projects", projects);
              }}
            />
          </ItemWrapper>
        )}
        <div className="mt-2">
          <label className="block text-sm font-semibold text-gray-800 py-2">
            {t("Project lead")}
          </label>
          <SelectUser
            onChange={(user) => {
              const userId = user && user !== null ? user.value : "";
              handleUpdateField("projectManager", userId);
            }}
            userId={project.get("projectManager")}
            isDisabled={!isAdmin}
          />
        </div>
        <div className="mt-2">
          <CurrencyPicker
            currencyCode={project.get("currency")}
            onChange={(currency: string) => {
              handleUpdateField("currency", currency);
            }}
            isDisabled={!isAdmin}
          />
        </div>
        {isAdmin ? (
          <div className="mt-4 flex items-center gap-4">
            <Button variant="danger" onClick={handleDelete}>
              {t("Delete")}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {t("Saving...")}
                </span>
              ) : (
                t("Save")
              )}
            </Button>
          </div>
        ) : null}
      </div>
      <ConfirmDialog
        visible={showConfirmDelete}
        onClose={() => {
          setShowConfirmDelete(false);
        }}
        title={t("Delete Project")}
        description={t("This will permanently delete this Pipeline.")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={() => {
          deleteCurrentProject();
        }}
        dangerBtn={true}
      />
    </div>
  );
};

export default GeneralSetting;
